/**
 * ConversationalCoach Component
 *
 * The main chat interface for the conversational discovery coach.
 * Features:
 * - Message bubbles with streaming support
 * - Quick reply pills (optional, can be ignored)
 * - Enter to send (no "Submit Answer" button)
 * - Mode toggle (guided vs organic)
 * - Topic coverage sidebar (collapsible)
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Loader2, AlertCircle, Sparkles, Lightbulb, Trash2, Download, ArrowRight, Target, Upload, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useConversationalCoach, type ChatMessage } from '@/hooks/useConversationalCoach'
import { useAIStore, CONVERSATION_MODES, type ConversationMode } from '@/stores/aiStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { discoveryApi, type Document2WithEntities } from '@/lib/api/client'
import { QuickReplyPills } from './QuickReplyPills'
import { TagSelector } from './TagSelector'
import { Documents3UploaderCompact } from '@/components/document-processing/Documents3UploaderCompact'

type OnboardingStep = 'goal' | 'idea'

interface ConversationalCoachProps {
  accountId: string
  projectId: string
  sessionId: string | undefined
  className?: string
}

export function ConversationalCoach({
  accountId,
  projectId,
  sessionId,
  className,
}: ConversationalCoachProps) {
  const { apiKey, conversationMode, setConversationMode } = useAIStore()
  const { fetchJourney, framework, hasGoal, selectGoal, getSelectedGoal } = useJourneyStore()
  const [inputValue, setInputValue] = useState('')
  const [onboardingIdea, setOnboardingIdea] = useState('')
  const [isStarting, setIsStarting] = useState(false)
  const [showDocUploader, setShowDocUploader] = useState(false)
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('goal')
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null)
  const [isSelectingGoal, setIsSelectingGoal] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Check if goal is already selected from journey store
  const goalAlreadySelected = hasGoal()
  const currentGoal = getSelectedGoal()

  // If goal is already selected, skip to idea step
  useEffect(() => {
    if (goalAlreadySelected && currentGoal) {
      setSelectedGoalId(currentGoal.id)
      setOnboardingStep('idea')
    }
  }, [goalAlreadySelected, currentGoal])

  const {
    messages,
    isLoading,
    isStreaming,
    error,
    mode,
    quickReplies,
    tagSelection,
    sendMessage,
    selectQuickReply,
    selectTags,
    skipTagSelection,
    setMode,
    clearMessages,
    isConfigured,
  } = useConversationalCoach(accountId, projectId, sessionId)

  // Sync mode with store
  useEffect(() => {
    setMode(conversationMode)
  }, [conversationMode, setMode])

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle send
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isStreaming) return
    const content = inputValue.trim()
    setInputValue('')
    await sendMessage(content)
  }, [inputValue, isLoading, isStreaming, sendMessage])

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Handle mode change
  const handleModeChange = (newMode: ConversationMode) => {
    setMode(newMode)
    setConversationMode(newMode)
  }

  // Ask AI for suggestions
  const handleAskSuggestions = useCallback(async () => {
    if (isLoading || isStreaming) return
    await sendMessage('What topics or insights should I explore next based on our conversation?')
  }, [isLoading, isStreaming, sendMessage])

  // Delete conversation history
  const handleDeleteHistory = useCallback(async () => {
    if (!sessionId || messages.length === 0) return
    if (!window.confirm('Are you sure you want to delete all messages? This cannot be undone.')) {
      return
    }
    try {
      await discoveryApi.deleteMessages(accountId, projectId, sessionId)
      clearMessages()
    } catch (err) {
      console.error('Failed to delete messages:', err)
    }
  }, [sessionId, messages.length, accountId, projectId, clearMessages])

  // Export conversation history
  const handleExport = useCallback((format: 'json' | 'markdown') => {
    if (!sessionId || messages.length === 0) return
    const url = discoveryApi.getExportUrl(accountId, projectId, sessionId, format)
    window.open(url, '_blank')
  }, [sessionId, messages.length, accountId, projectId])

  // Handle goal selection
  const handleSelectGoal = useCallback(async (goalId: string) => {
    setIsSelectingGoal(true)
    setSelectedGoalId(goalId)

    try {
      await selectGoal(accountId, projectId, goalId)
      setOnboardingStep('idea')
    } catch (err) {
      setSelectedGoalId(null)
    } finally {
      setIsSelectingGoal(false)
    }
  }, [accountId, projectId, selectGoal])

  // Start discovery with idea text
  const handleStartDiscovery = useCallback(async () => {
    if (!onboardingIdea.trim()) return
    setIsStarting(true)

    try {
      // Include selected goal context in the first message
      const goal = framework?.goals.find(g => g.id === selectedGoalId)
      const goalContext = goal
        ? `My goal is: ${goal.name} - ${goal.shortDescription}\n\n`
        : ''
      const ideaMessage = `${goalContext}Here's my business idea:\n\n${onboardingIdea.trim()}`
      await sendMessage(ideaMessage)
      setOnboardingIdea('')
    } finally {
      setIsStarting(false)
    }
  }, [onboardingIdea, sendMessage, framework, selectedGoalId])

  // Handle document upload completion - start discovery with extracted entities
  const handleDocumentComplete = useCallback(async (doc: Document2WithEntities) => {
    setShowDocUploader(false)

    // Refresh journey to pick up new entity counts from graph
    await fetchJourney(accountId, projectId)

    // Build a message with extracted entities
    const entitySummary = doc.entities && doc.entities.length > 0
      ? `\n\nExtracted entities:\n${doc.entities.map(e => `- ${e.entityType}: ${e.name}`).join('\n')}`
      : ''

    const message = `I've uploaded a document: "${doc.document.title}"${entitySummary}\n\nPlease help me analyze this and discover key insights for my business.`
    await sendMessage(message)
  }, [sendMessage, fetchJourney, accountId, projectId])

  // Check if we can start discovery
  const canStartDiscovery = onboardingIdea.trim().length > 0

  // Not configured message
  if (!apiKey) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full p-8 text-center', className)}>
        <div className="w-12 h-12 rounded-full bg-warm-100 flex items-center justify-center mb-4">
          <AlertCircle className="w-6 h-6 text-warm-500" />
        </div>
        <h3 className="text-lg font-semibold text-warm-700 mb-2">AI Not Configured</h3>
        <p className="text-sm text-text-muted max-w-sm">
          Add your Claude API key in Settings to start conversing with the discovery coach.
        </p>
      </div>
    )
  }

  // No session message
  if (!sessionId) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full p-8 text-center', className)}>
        <div className="w-12 h-12 rounded-full bg-solution/10 flex items-center justify-center mb-4">
          <Sparkles className="w-6 h-6 text-solution" />
        </div>
        <h3 className="text-lg font-semibold text-warm-700 mb-2">Start a Discovery Session</h3>
        <p className="text-sm text-text-muted max-w-sm">
          Create or select a discovery session to begin your conversation.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex h-full', className)}>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-warm-200 bg-gradient-to-r from-warm-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-market to-market/80 flex items-center justify-center shadow-sm">
              <Sparkles className="w-[18px] h-[18px] text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-warm-800">Discovery Coach</h2>
              <p className="text-xs text-text-muted">Let&apos;s explore your idea together</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <select
              value={mode}
              onChange={(e) => handleModeChange(e.target.value as ConversationMode)}
              className="text-xs border border-warm-200 rounded-md px-2 py-1 bg-white text-warm-700 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {CONVERSATION_MODES.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            {/* Export button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleExport('markdown')}
              disabled={messages.length === 0}
              className="h-8 w-8 p-0"
              title="Export conversation"
            >
              <Download className="h-4 w-4 text-warm-500" />
            </Button>

            {/* Delete button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteHistory}
              disabled={messages.length === 0}
              className="h-8 w-8 p-0"
              title="Delete conversation"
            >
              <Trash2 className="h-4 w-4 text-warm-500 hover:text-red-500" />
            </Button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Onboarding form if no messages */}
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full p-4">
              <div className="w-full max-w-lg bg-white rounded-2xl border border-warm-200 shadow-sm p-6">
                {/* Step indicator */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className={cn(
                    'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                    onboardingStep === 'goal' ? 'bg-market/10 text-market' : 'bg-warm-100 text-warm-500'
                  )}>
                    <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">1</span>
                    Goal
                  </div>
                  <ArrowRight className="w-4 h-4 text-warm-300" />
                  <div className={cn(
                    'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                    onboardingStep === 'idea' ? 'bg-market/10 text-market' : 'bg-warm-100 text-warm-400'
                  )}>
                    <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">2</span>
                    Idea
                  </div>
                </div>

                {/* STEP 1: Goal Selection */}
                {onboardingStep === 'goal' && (
                  <>
                    <div className="flex items-start gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-market/10 flex items-center justify-center flex-shrink-0">
                        <Target className="w-5 h-5 text-market" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-warm-800">What&apos;s your goal?</h3>
                        <p className="text-sm text-text-muted">Choose the journey that best fits your situation</p>
                      </div>
                    </div>

                    {/* Goal options */}
                    <div className="space-y-2">
                      {framework?.goals.map((goal) => (
                        <button
                          key={goal.id}
                          onClick={() => handleSelectGoal(goal.id)}
                          disabled={isSelectingGoal}
                          className={cn(
                            'w-full text-left p-4 rounded-xl border-2 transition-all',
                            'hover:border-market hover:bg-market/5',
                            selectedGoalId === goal.id && isSelectingGoal
                              ? 'border-market bg-market/10'
                              : 'border-warm-200 bg-white'
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-xl">{goal.emoji}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-warm-800">{goal.name}</div>
                              <div className="text-sm text-text-muted mt-0.5">{goal.shortDescription}</div>
                              {goal.recommendedFor && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {goal.recommendedFor.map((tag) => (
                                    <span key={tag} className="px-2 py-0.5 bg-warm-100 text-warm-600 text-xs rounded-full">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {selectedGoalId === goal.id && isSelectingGoal && (
                              <Loader2 className="w-5 h-5 text-market animate-spin" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* STEP 2: Idea or File Upload */}
                {onboardingStep === 'idea' && (
                  <>
                    <div className="flex items-start gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-market/10 flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-5 h-5 text-market" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-warm-800">Share your idea</h3>
                        <p className="text-sm text-text-muted">Describe your idea or upload existing materials</p>
                      </div>
                    </div>

                    {/* Selected goal badge */}
                    {selectedGoalId && framework && (
                      <div className="mb-4 p-3 bg-warm-50 rounded-lg border border-warm-200">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-warm-500">Goal:</span>
                          <span className="font-medium text-warm-700">
                            {framework.goals.find(g => g.id === selectedGoalId)?.emoji}{' '}
                            {framework.goals.find(g => g.id === selectedGoalId)?.name}
                          </span>
                          <button
                            onClick={() => {
                              setOnboardingStep('goal')
                              setSelectedGoalId(null)
                            }}
                            className="ml-auto text-xs text-warm-500 hover:text-market"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Enter idea */}
                    <div className="mb-4">
                      <label className="flex items-center gap-2 text-sm font-medium text-warm-700 mb-2">
                        <FileText className="w-4 h-4 text-warm-500" />
                        Enter your idea
                      </label>
                      <textarea
                        value={onboardingIdea}
                        onChange={(e) => setOnboardingIdea(e.target.value)}
                        placeholder="e.g., A platform that helps small restaurants manage their online orders..."
                        rows={3}
                        className="w-full resize-none rounded-lg border border-warm-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-market/30 focus:border-market placeholder:text-warm-400"
                        disabled={isStarting}
                      />
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-warm-200" />
                      <span className="text-xs text-warm-400 font-medium">or</span>
                      <div className="flex-1 h-px bg-warm-200" />
                    </div>

                    {/* Upload document */}
                    {showDocUploader ? (
                      <div className="mb-4">
                        <Documents3UploaderCompact
                          onComplete={handleDocumentComplete}
                          onError={(err) => {
                            console.error('Upload error:', err)
                            // Error is already displayed by the Documents3UploaderCompact component
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDocUploader(false)}
                          className="w-full mt-2 text-warm-500"
                        >
                          Cancel upload
                        </Button>
                      </div>
                    ) : (
                      <div className="mb-6">
                        <Button
                          variant="outline"
                          onClick={() => setShowDocUploader(true)}
                          className="w-full border-dashed border-warm-300 text-warm-600 hover:border-market hover:text-market hover:bg-market/5"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload document (pitch deck, PRD, notes...)
                        </Button>
                      </div>
                    )}

                    {/* Start button */}
                    {!showDocUploader && (
                      <>
                        <Button
                          onClick={handleStartDiscovery}
                          disabled={!canStartDiscovery || isStarting}
                          className="w-full bg-market hover:bg-market/90 text-white gap-2"
                        >
                          {isStarting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              Start Discovery
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-warm-400 text-center mt-3">
                          Enter an idea to start, or upload a document
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Message list */}
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}

          {/* Quick replies */}
          {quickReplies.length > 0 && !isStreaming && (
            <div className="pl-12">
              <QuickReplyPills
                replies={quickReplies}
                onSelect={selectQuickReply}
                disabled={isLoading || isStreaming}
              />
            </div>
          )}

          {/* Tag selection */}
          {tagSelection && !isStreaming && (
            <div className="pl-12">
              <TagSelector
                selection={tagSelection}
                onSubmit={selectTags}
                onSkip={skipTagSelection}
                disabled={isLoading || isStreaming}
              />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {typeof error === 'string' ? error : error.message || 'An error occurred'}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - only show when chat is active */}
        {messages.length > 0 && (
          <div className="p-4 border-t border-warm-200 bg-white">
            <div className="relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isStreaming
                    ? 'Coach is thinking...'
                    : 'Share your thoughts or ask a question...'
                }
                rows={2}
                className="w-full resize-none rounded-xl border border-warm-300 px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-warm-400"
                disabled={isLoading || isStreaming}
              />

              {/* Send button */}
              <Button
                size="sm"
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading || isStreaming}
                className="absolute right-2 bottom-2 h-8 w-8 p-0 rounded-full"
              >
                {isLoading || isStreaming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Hints and Actions */}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-warm-400">
                <span>Enter to send</span>
                <span>Shift+Enter for new line</span>
              </div>

              {/* Ask for Suggestions button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAskSuggestions}
                disabled={isLoading || isStreaming || messages.length === 0}
                className="h-7 px-3 text-xs text-warm-500 hover:text-market hover:bg-market/10 gap-1.5"
                title="Ask AI for topic suggestions"
              >
                <Lightbulb className="h-3.5 w-3.5" />
                Get suggestions
              </Button>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

// Message Bubble Component
function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  return (
    <div className={cn('flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-market to-market/80 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-[14px] h-[14px] text-white" />
        </div>
      )}

      {/* Message content */}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-4 py-3',
          isUser && 'bg-warm-800 text-white rounded-tr-sm',
          !isUser && !isSystem && 'bg-white border border-warm-200 rounded-tl-sm shadow-sm',
          isSystem && 'bg-warm-100 text-text-secondary text-sm italic',
          message.isStreaming && 'animate-pulse'
        )}
      >
        {/* Content */}
        <div className="whitespace-pre-wrap text-sm leading-relaxed">
          {message.content || (message.isStreaming ? '...' : '')}
        </div>

        {/* Streaming indicator */}
        {message.isStreaming && message.content && (
          <span className="inline-block w-1.5 h-4 bg-market ml-0.5 animate-pulse rounded-sm" />
        )}

        {/* Timestamp */}
        <div
          className={cn(
            'text-[10px] mt-1.5',
            isUser ? 'text-white/70' : 'text-text-muted'
          )}
        >
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  )
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}
